/**
 * Bulk Sync Hook
 * Processes multiple unsynced accounts sequentially to avoid Cloudflare limits
 */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// API configuration
const API_BASE = '/api';

function getAuthToken(): string | null {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch {
    return null;
  }
  return null;
}

export interface BulkSyncAccount {
  id: string;
  name: string;
  platform: string;
}

export interface BulkSyncProgress {
  currentAccountIndex: number;
  totalAccounts: number;
  currentAccountName: string;
  currentPhase: 'idle' | 'fetching_history' | 'syncing_players' | 'waiting' | 'complete' | 'error';
  currentIteration: number;
  totalIterations: number;
  monthsSynced: number;
  playersSynced: number;
  completedAccounts: string[];
  failedAccounts: { id: string; name: string; error: string }[];
  isRunning: boolean;
  startTime: Date | null;
  estimatedTimeRemaining: string | null;
}

interface HistoricalSyncResult {
  success: boolean;
  summary?: {
    months_synced_now: number;
    total_players_synced: number;
    has_more_months: boolean;
    remaining_months: number;
    next_offset?: number;
    first_data_month?: string;
    hit_account_start_boundary?: boolean;
  };
}

export function useBulkSync() {
  const queryClient = useQueryClient();
  const abortRef = useRef(false);
  
  const [progress, setProgress] = useState<BulkSyncProgress>({
    currentAccountIndex: 0,
    totalAccounts: 0,
    currentAccountName: '',
    currentPhase: 'idle',
    currentIteration: 0,
    totalIterations: 0,
    monthsSynced: 0,
    playersSynced: 0,
    completedAccounts: [],
    failedAccounts: [],
    isRunning: false,
    startTime: null,
    estimatedTimeRemaining: null,
  });

  const calculateETA = useCallback((completedCount: number, totalCount: number, startTime: Date) => {
    if (completedCount === 0) return 'Calculating...';
    const elapsedMs = Date.now() - startTime.getTime();
    const avgTimePerAccount = elapsedMs / completedCount;
    const remainingAccounts = totalCount - completedCount;
    const remainingMs = avgTimePerAccount * remainingAccounts;
    
    if (remainingMs < 60000) {
      return `${Math.ceil(remainingMs / 1000)} seconds`;
    } else if (remainingMs < 3600000) {
      return `${Math.ceil(remainingMs / 60000)} minutes`;
    } else {
      return `${Math.floor(remainingMs / 3600000)}h ${Math.ceil((remainingMs % 3600000) / 60000)}m`;
    }
  }, []);

  const runHistoricalSync = async (
    networkId: string,
    force: boolean = true,
    skipPlayers: boolean = true,
    onProgress?: (iteration: number, hasMore: boolean) => void
  ): Promise<{ success: boolean; monthsSynced: number; error?: string }> => {
    const token = getAuthToken();
    let totalMonthsSynced = 0;
    let currentOffset = 0;
    let iterations = 0;
    const MAX_ITERATIONS = 20;

    while (iterations < MAX_ITERATIONS && !abortRef.current) {
      iterations++;
      onProgress?.(iterations, true);

      const params = new URLSearchParams();
      if (force) params.append('force', 'true');
      if (skipPlayers) params.append('skip_players', 'true');
      if (currentOffset > 0) params.append('offset', currentOffset.toString());

      try {
        const response = await fetch(`${API_BASE}/networks/${networkId}/historical-sync?${params.toString()}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          const errorData = await response.json() as { message?: string };
          return { success: false, monthsSynced: totalMonthsSynced, error: errorData.message || 'API error' };
        }

        const result = await response.json() as HistoricalSyncResult;
        totalMonthsSynced += result.summary?.months_synced_now || 0;

        if (!result.summary?.has_more_months || result.summary.remaining_months === 0) {
          onProgress?.(iterations, false);
          break;
        }

        // Update offset for next iteration
        currentOffset = result.summary.next_offset ?? (currentOffset + 6);
        
        // Delay between iterations to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (error: any) {
        return { success: false, monthsSynced: totalMonthsSynced, error: error.message };
      }
    }

    return { success: true, monthsSynced: totalMonthsSynced };
  };

  const runPlayerSync = async (
    networkId: string,
    onProgress?: (iteration: number, hasMore: boolean) => void
  ): Promise<{ success: boolean; playersSynced: number; error?: string }> => {
    const token = getAuthToken();
    let totalPlayersSynced = 0;
    const currentOffset = 0;
    let iterations = 0;
    const MAX_ITERATIONS = 10; // Player sync is usually faster

    while (iterations < MAX_ITERATIONS && !abortRef.current) {
      iterations++;
      onProgress?.(iterations, true);

      const params = new URLSearchParams();
      params.append('skip_players', 'false'); // Explicitly sync players
      params.append('skip_stats', 'true'); // Skip stats, only sync players
      params.append('start_date', '2019-01-01'); // Fetch all historical players
      if (currentOffset > 0) params.append('offset', currentOffset.toString());

      try {
        const response = await fetch(`${API_BASE}/networks/${networkId}/historical-sync?${params.toString()}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          const errorData = await response.json() as { message?: string };
          return { success: false, playersSynced: totalPlayersSynced, error: errorData.message || 'API error' };
        }

        const result = await response.json() as HistoricalSyncResult;
        totalPlayersSynced += result.summary?.total_players_synced || 0;

        // Player sync completes in a single request (pagination handled internally)
        // Break after first successful iteration
        onProgress?.(iterations, false);
        break;
      } catch (error: any) {
        return { success: false, playersSynced: totalPlayersSynced, error: error.message };
      }
    }

    return { success: true, playersSynced: totalPlayersSynced };
  };

  const startBulkSync = useCallback(async (accounts: BulkSyncAccount[]) => {
    if (accounts.length === 0) return;

    abortRef.current = false;
    const startTime = new Date();

    setProgress({
      currentAccountIndex: 0,
      totalAccounts: accounts.length,
      currentAccountName: accounts[0].name,
      currentPhase: 'fetching_history',
      currentIteration: 0,
      totalIterations: 0,
      monthsSynced: 0,
      playersSynced: 0,
      completedAccounts: [],
      failedAccounts: [],
      isRunning: true,
      startTime,
      estimatedTimeRemaining: 'Calculating...',
    });

    const completedAccounts: string[] = [];
    const failedAccounts: { id: string; name: string; error: string }[] = [];
    let totalMonthsSynced = 0;
    let totalPlayersSynced = 0;

    for (let i = 0; i < accounts.length; i++) {
      if (abortRef.current) break;

      const account = accounts[i];

      // Update progress for this account
      setProgress(prev => ({
        ...prev,
        currentAccountIndex: i,
        currentAccountName: account.name,
        currentPhase: 'fetching_history',
        currentIteration: 0,
        estimatedTimeRemaining: calculateETA(completedAccounts.length, accounts.length, startTime),
      }));

      // Phase 1: Historical Sync (Force Resync)
      console.log(`[BulkSync] Starting Force Resync for ${account.name} (${i + 1}/${accounts.length})`);
      
      const historyResult = await runHistoricalSync(
        account.id,
        true,  // force
        true,  // skip players initially
        (iteration, hasMore) => {
          setProgress(prev => ({
            ...prev,
            currentIteration: iteration,
            currentPhase: 'fetching_history',
          }));
        }
      );

      if (!historyResult.success) {
        console.error(`[BulkSync] Force Resync failed for ${account.name}: ${historyResult.error}`);
        failedAccounts.push({ id: account.id, name: account.name, error: historyResult.error || 'Unknown error' });
        setProgress(prev => ({
          ...prev,
          failedAccounts: [...prev.failedAccounts, { id: account.id, name: account.name, error: historyResult.error || 'Unknown error' }],
        }));
        
        // Wait before next account
        await new Promise(resolve => setTimeout(resolve, 3000));
        continue;
      }

      totalMonthsSynced += historyResult.monthsSynced;

      // Phase 2: Player Sync
      if (!abortRef.current) {
        setProgress(prev => ({
          ...prev,
          currentPhase: 'syncing_players',
          currentIteration: 0,
          monthsSynced: totalMonthsSynced,
        }));

        console.log(`[BulkSync] Starting Player Sync for ${account.name}`);
        
        const playerResult = await runPlayerSync(
          account.id,
          (iteration, hasMore) => {
            setProgress(prev => ({
              ...prev,
              currentIteration: iteration,
              currentPhase: 'syncing_players',
            }));
          }
        );

        if (playerResult.success) {
          totalPlayersSynced += playerResult.playersSynced;
        } else {
          console.warn(`[BulkSync] Player sync failed for ${account.name}: ${playerResult.error}`);
          // Don't mark as failed if only player sync fails
        }
      }

      // Mark as completed
      completedAccounts.push(account.id);
      setProgress(prev => ({
        ...prev,
        completedAccounts: [...prev.completedAccounts, account.id],
        monthsSynced: totalMonthsSynced,
        playersSynced: totalPlayersSynced,
        estimatedTimeRemaining: calculateETA(completedAccounts.length, accounts.length, startTime),
      }));

      // Wait between accounts to avoid rate limiting
      if (i < accounts.length - 1 && !abortRef.current) {
        setProgress(prev => ({ ...prev, currentPhase: 'waiting' }));
        console.log(`[BulkSync] Waiting 5 seconds before next account...`);
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }

    // Complete
    setProgress(prev => ({
      ...prev,
      currentPhase: abortRef.current ? 'idle' : 'complete',
      isRunning: false,
      estimatedTimeRemaining: null,
    }));

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ['networks'] });
    queryClient.invalidateQueries({ queryKey: ['statistics'] });

    console.log(`[BulkSync] Complete! ${completedAccounts.length} accounts synced, ${failedAccounts.length} failed`);
  }, [calculateETA, queryClient]);

  /**
   * Bulk Player Sync - Only syncs players for all accounts (no statistics)
   * Useful for updating player data without re-fetching all historical stats
   */
  const startBulkPlayerSync = useCallback(async (accounts: BulkSyncAccount[]) => {
    if (accounts.length === 0) return;

    abortRef.current = false;
    const startTime = new Date();

    setProgress({
      currentAccountIndex: 0,
      totalAccounts: accounts.length,
      currentAccountName: accounts[0].name,
      currentPhase: 'syncing_players',
      currentIteration: 0,
      totalIterations: 0,
      monthsSynced: 0,
      playersSynced: 0,
      completedAccounts: [],
      failedAccounts: [],
      isRunning: true,
      startTime,
      estimatedTimeRemaining: 'Calculating...',
    });

    const completedAccounts: string[] = [];
    const failedAccounts: { id: string; name: string; error: string }[] = [];
    let totalPlayersSynced = 0;

    for (let i = 0; i < accounts.length; i++) {
      if (abortRef.current) break;

      const account = accounts[i];

      // Update progress for this account
      setProgress(prev => ({
        ...prev,
        currentAccountIndex: i,
        currentAccountName: account.name,
        currentPhase: 'syncing_players',
        currentIteration: 0,
        estimatedTimeRemaining: calculateETA(completedAccounts.length, accounts.length, startTime),
      }));

      console.log(`[BulkPlayerSync] Starting Player Sync for ${account.name} (${i + 1}/${accounts.length})`);
      
      const playerResult = await runPlayerSync(
        account.id,
        (iteration, hasMore) => {
          setProgress(prev => ({
            ...prev,
            currentIteration: iteration,
            currentPhase: 'syncing_players',
          }));
        }
      );

      if (playerResult.success) {
        totalPlayersSynced += playerResult.playersSynced;
        completedAccounts.push(account.id);
        setProgress(prev => ({
          ...prev,
          completedAccounts: [...prev.completedAccounts, account.id],
          playersSynced: totalPlayersSynced,
          estimatedTimeRemaining: calculateETA(completedAccounts.length, accounts.length, startTime),
        }));
        console.log(`[BulkPlayerSync] Completed ${account.name}: ${playerResult.playersSynced} players`);
      } else {
        console.error(`[BulkPlayerSync] Failed for ${account.name}: ${playerResult.error}`);
        failedAccounts.push({ id: account.id, name: account.name, error: playerResult.error || 'Unknown error' });
        setProgress(prev => ({
          ...prev,
          failedAccounts: [...prev.failedAccounts, { id: account.id, name: account.name, error: playerResult.error || 'Unknown error' }],
        }));
      }

      // Wait between accounts to avoid rate limiting
      if (i < accounts.length - 1 && !abortRef.current) {
        setProgress(prev => ({ ...prev, currentPhase: 'waiting' }));
        console.log(`[BulkPlayerSync] Waiting 3 seconds before next account...`);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    // Complete
    setProgress(prev => ({
      ...prev,
      currentPhase: abortRef.current ? 'idle' : 'complete',
      isRunning: false,
      estimatedTimeRemaining: null,
    }));

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ['networks'] });
    queryClient.invalidateQueries({ queryKey: ['players'] });

    console.log(`[BulkPlayerSync] Complete! ${completedAccounts.length} accounts synced, ${failedAccounts.length} failed, ${totalPlayersSynced} total players`);
  }, [calculateETA, queryClient]);

  const stopBulkSync = useCallback(() => {
    abortRef.current = true;
    setProgress(prev => ({
      ...prev,
      isRunning: false,
      currentPhase: 'idle',
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setProgress({
      currentAccountIndex: 0,
      totalAccounts: 0,
      currentAccountName: '',
      currentPhase: 'idle',
      currentIteration: 0,
      totalIterations: 0,
      monthsSynced: 0,
      playersSynced: 0,
      completedAccounts: [],
      failedAccounts: [],
      isRunning: false,
      startTime: null,
      estimatedTimeRemaining: null,
    });
  }, []);

  return {
    progress,
    startBulkSync,
    startBulkPlayerSync,
    stopBulkSync,
    resetProgress,
  };
}
